/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import { token } from '@atlaskit/tokens';

import { TWG_BRANDING } from '../../../../../branding';
import { AGENT_PILLS, TWG_CLI_DOCS_URL } from '../../../../../constants';
import { messages } from '../messages';

import { AgentPill } from './AgentPill';

const styles = cssMap({
	worksWith: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.300'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		minHeight: '360px',
		paddingInline: token('space.1000'),
		paddingBlock: token('space.500'),
		'@media (max-width: 30rem)': {
			paddingInline: token('space.0'),
		},
	},
	worksWithTitle: {
		margin: 0,
		font: token('font.heading.large'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#FFFFFF',
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '36px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
		textAlign: 'center',
		fontVariantNumeric: 'tabular-nums lining-nums',
	},
	pillRow: {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.150'),
		width: '100%',
		maxWidth: '960px',
	},
	worksWithFootnote: {
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#7D818A',
		font: token('font.body'),
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		textAlign: 'center',
	},
	viewDocsButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.075'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#292A2E',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.body'),
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		transition:
			'background-color 150ms ease, border-color 150ms ease, color 150ms ease, transform 150ms ease',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: 'rgba(255, 255, 255, 0.06)',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			borderColor: '#44474C',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			color: '#F0F1F2',
		},
		'&:active': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: 'rgba(255, 255, 255, 0.12)',
			transform: 'scale(0.98)',
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
});

export const WorksWith = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.worksWith}>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives, @atlaskit/design-system/use-heading */}
			<h3 css={styles.worksWithTitle}>
				<FormattedMessage {...messages.worksWith} />
			</h3>
			<ul css={styles.pillRow}>
				{AGENT_PILLS.map((pill) => (
					<AgentPill key={pill.label} {...pill} />
				))}
			</ul>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p css={styles.worksWithFootnote}>
				<FormattedMessage {...messages.plusAnyApp} />
			</p>
			{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor, @atlassian/a11y/anchor-is-valid */}
			<a
				aria-label={formatMessage(messages.viewDocsAriaLabel, {
					twg_branding: TWG_BRANDING,
				})}
				css={styles.viewDocsButton}
				target="_blank"
				href={TWG_CLI_DOCS_URL}
				rel="noopener noreferrer"
			>
				<FormattedMessage {...messages.viewDocs} />
				<ArrowUpRightIcon label="" color="currentColor" />
			</a>
		</div>
	);
};
